import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { assetsApi, peopleApi, raciApi } from '../services/api';
import { useToast } from '../hooks/useToast';
import { useSwal } from '../hooks/useSwal';
import { PageHeader } from '../components/PageHeader';
import { NimbusTable } from '../components/NimbusTable';
import { SEVERITY_TONE, SENSITIVITY_TONE, SENSITIVITY_LABELS, fuzzyStatusTone } from '../constants/tone';

type Asset = {
  assetId: string;
  name?: string;
  type?: string;
  description?: string;
  owner?: string;
  location?: string;
  parentAssetId?: string | null;
  serialNumber?: string;
  manufacturer?: string;
  model?: string;
  purchaseDate?: string;
  purchaseCost?: number;
  classification?: string;
  criticality?: string;
  status?: string;
  notes?: string;
};

export const AssetsPage: React.FC = () => {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const toast = useToast();
  const swal = useSwal();

  const { data: assets = [], isLoading, error } = useQuery<Asset[]>({
    queryKey: ['assets'],
    queryFn: assetsApi.getAll,
  });

  const { data: people = [] } = useQuery<any[]>({
    queryKey: ['people'],
    queryFn: peopleApi.getAll,
  });

  const personMap = useMemo(() => {
    const map: Record<string, string> = {};
    people.forEach((p: any) => {
      if (p.personId) {
        map[p.personId] = [p.firstName, p.lastName].filter(Boolean).join(' ') || p.email || p.personId;
      }
    });
    return map;
  }, [people]);

  const resolveOwner = (owner?: string) => {
    if (!owner) return '-';
    if (personMap[owner]) return personMap[owner];
    return /^PERSON-/i.test(owner) ? 'Unknown User' : owner;
  };

  // ── Parent/child cascade ─────────────────────────────────────────────────
  const assetById = useMemo(() => {
    const map = new Map<string, Asset>();
    assets.forEach((a) => map.set(a.assetId, a));
    return map;
  }, [assets]);

  const childrenByParent = useMemo(() => {
    const map = new Map<string, Asset[]>();
    assets.forEach((a) => {
      if (a.parentAssetId && assetById.has(a.parentAssetId)) {
        if (!map.has(a.parentAssetId)) map.set(a.parentAssetId, []);
        map.get(a.parentAssetId)!.push(a);
      }
    });
    return map;
  }, [assets, assetById]);

  // Assets with no parent, or whose parent no longer exists, are treated as roots
  // so nothing silently disappears from the register.
  const rootAssets = useMemo(
    () => assets.filter((a) => !a.parentAssetId || !assetById.has(a.parentAssetId)),
    [assets, assetById],
  );

  const [collapsedIds, setCollapsedIds] = useState<Set<string>>(new Set());
  const toggleCollapsed = (assetIdToToggle: string) => {
    setCollapsedIds((prev) => {
      const next = new Set(prev);
      if (next.has(assetIdToToggle)) next.delete(assetIdToToggle);
      else next.add(assetIdToToggle);
      return next;
    });
  };

  const deleteMutation = useMutation({
    mutationFn: async (a: Asset) => {
      await assetsApi.delete(a.assetId);
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['assets'] });
      toast.success('Asset deleted.');
    },
    onError: (e: Error) => toast.error(`Failed to delete: ${e.message}`),
  });

  const handleDelete = async (a: Asset) => {
    const ok = await swal.confirmDelete(a.name || a.assetId);
    if (!ok) return;
    deleteMutation.mutate(a);
  };

  const duplicateMutation = useMutation({
    mutationFn: async (a: Asset) => {
      const payload: any = {
        name: `${a.name || a.assetId} (Copy)`,
        type: a.type || null,
        description: a.description || null,
        owner: a.owner || null,
        location: a.location || null,
        parentAssetId: a.parentAssetId || null,
        serialNumber: a.serialNumber || null,
        manufacturer: a.manufacturer || null,
        model: a.model || null,
        purchaseDate: a.purchaseDate || null,
        purchaseCost: a.purchaseCost || 0,
        classification: a.classification || 'internal',
        criticality: a.criticality || 'Medium',
        status: a.status || 'active',
        notes: a.notes || null,
      };
      const result = await assetsApi.create(payload);
      const newAssetId = result.asset?.assetId ?? result.assetId;

      // Carry over the RACI assignments too -- that's the whole point of duplicating
      // rather than starting from scratch (same people/roles apply to similar assets).
      const raciAssignments = await raciApi.getByArtefact('asset', a.assetId);
      if (raciAssignments.length) {
        await raciApi.batchCreate(
          raciAssignments.map((r: any) => ({
            artefactType: 'asset',
            artefactId: newAssetId,
            personId: r.personId,
            role: r.role,
            notes: r.notes,
          })),
        );
      }
      return { newAssetId, raciCount: raciAssignments.length };
    },
    onSuccess: async ({ newAssetId, raciCount }: { newAssetId: string; raciCount: number }) => {
      await qc.invalidateQueries({ queryKey: ['assets'] });
      toast.success(`Asset duplicated${raciCount ? ` with ${raciCount} RACI assignment${raciCount === 1 ? '' : 's'}` : ''}.`);
      if (newAssetId) navigate(`/assets/${newAssetId}/edit`);
    },
    onError: (e: Error) => toast.error(`Failed to duplicate: ${e.message}`),
  });

  const renderAssetRows = (list: Asset[], depth: number): React.ReactNode[] =>
    list.flatMap((a) => {
      const children = childrenByParent.get(a.assetId) || [];
      const hasChildren = children.length > 0;
      const isCollapsed = collapsedIds.has(a.assetId);

      const row = (
        <tr key={a.assetId}>
          <td className="np-row-title-static">
            <div className="d-flex align-items-center" style={{ paddingLeft: depth * 24 }}>
              {hasChildren ? (
                <button
                  type="button"
                  className="np-icon-btn me-1"
                  onClick={() => toggleCollapsed(a.assetId)}
                  title={isCollapsed ? 'Expand' : 'Collapse'}
                >
                  <i className={isCollapsed ? 'ri-arrow-right-s-line' : 'ri-arrow-down-s-line'}></i>
                </button>
              ) : (
                <span style={{ display: 'inline-block', width: '1.5rem' }}></span>
              )}
              {depth > 0 && <i className="ri-corner-down-right-line text-muted me-1" style={{ fontSize: '0.8rem' }}></i>}
              <span>{a.name || a.assetId}</span>
              {hasChildren && <span className="np-chip np-tone-neutral ms-2">{children.length}</span>}
            </div>
          </td>
          <td>{a.type ? <span className="np-chip np-tone-neutral">{a.type}</span> : <span className="np-sub">—</span>}</td>
          <td>{resolveOwner(a.owner)}</td>
          <td>
            <span className={`np-chip np-tone-${SENSITIVITY_TONE[a.classification as keyof typeof SENSITIVITY_TONE] ?? SENSITIVITY_TONE.internal}`}>
              {SENSITIVITY_LABELS[a.classification as keyof typeof SENSITIVITY_LABELS] ?? SENSITIVITY_LABELS.internal}
            </span>
          </td>
          <td>
            <span className={`np-chip np-tone-${SEVERITY_TONE[(a.criticality as keyof typeof SEVERITY_TONE) || 'Medium'] ?? SEVERITY_TONE.Medium}`}>
              {a.criticality || 'Medium'}
            </span>
          </td>
          <td>
            <span className={`np-chip np-tone-${fuzzyStatusTone(a.status || 'active')}`}>
              {a.status || 'active'}
            </span>
          </td>
          <td>
            <div className="d-flex gap-1">
              <button
                className="np-icon-btn"
                onClick={() => navigate(`/assets/${a.assetId}/edit`)}
                title="Edit"
              >
                <i className="ri-edit-line"></i>
              </button>
              <button
                className="np-icon-btn"
                onClick={() => duplicateMutation.mutate(a)}
                disabled={duplicateMutation.isPending}
                title="Duplicate (copies RACI assignments too)"
              >
                <i className="ri-file-copy-line"></i>
              </button>
              <button
                className="np-icon-btn np-icon-btn-danger"
                onClick={() => handleDelete(a)}
                disabled={deleteMutation.isPending}
                title="Delete"
              >
                <i className="ri-delete-bin-line"></i>
              </button>
            </div>
          </td>
        </tr>
      );

      return hasChildren && !isCollapsed ? [row, ...renderAssetRows(children, depth + 1)] : [row];
    });

  return (
    <div className="container-xxl flex-grow-1 container-p-y np-page">
      <PageHeader
        icon="ri-server-line"
        title="Assets"
        colour="warning"
        actions={
          <button className="np-btn np-btn-primary" onClick={() => navigate('/assets/new')}>
            <i className="ri-add-line"></i>Create Asset
          </button>
        }
      />

      <div className="np-card">
        <div className="np-card-body">
          {isLoading ? (
            <div className="d-flex justify-content-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : error ? (
            <div className="np-banner np-tone-danger mb-0"><i className="ri-error-warning-line"></i>Failed to load assets.</div>
          ) : assets.length === 0 ? (
            <div className="np-sub">No assets found.</div>
          ) : (
            <NimbusTable thead={<tr>
                    <th>Name</th>
                    <th style={{ width: '180px' }}>Type</th>
                    <th style={{ width: '220px' }}>Owner</th>
                    <th style={{ width: '150px' }}>Classification</th>
                    <th style={{ width: '120px' }}>Criticality</th>
                    <th style={{ width: '140px' }}>Status</th>
                    <th style={{ width: '180px' }}></th>
                  </tr>}>
                  {renderAssetRows(rootAssets, 0)}
            </NimbusTable>
          )}
        </div>
      </div>
    </div>
  );
};
